import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { gsap } from "gsap";
import { DrawSVGPlugin } from "gsap/DrawSVGPlugin";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { useAppSceneContext } from '../SceneState/SceneState';
import useWindowDimensions from '../../hooks/getWindowDimensions';
import sendPostMessage from '../../sendPostMessage';



import lottie, { AnimationItem } from "lottie-web";

import './Scene1.scss';
import './Scene2.scss';
import './Scene3.scss';

gsap.registerPlugin(DrawSVGPlugin, ScrollTrigger, ScrollToPlugin);

function Scene1() {
    const windowDimensions = useWindowDimensions();
    const [animationsLoading, setAnimationsLoading] = useState(false);
    const [dontTrack, setDontTrack] = useState(true);
    const [message, setMessage] = useState("");
    const [sceneHeight, setSceneHeight] = useState(0);
    const { act, setAct, gotoScene, setGotoScene, scene, setScene, setAniHeight } = useAppSceneContext();

    const [animations, setAnimations] = useState<{ loader: gsap.core.Timeline, scene: gsap.core.Timeline, scroller: null | gsap.core.Tween }>({
        loader: gsap.timeline({ useFrames: false, paused: true, repeat: 0, ease: "none", smoothChildTiming: true }),
        scene: gsap.timeline({ useFrames: false, paused: true, repeat: 0, ease: "none", smoothChildTiming: true }),
        scroller: null
    });

    const currentAni: string | any[] = useMemo(() => { return [] }, []);
    const gwsDemoFrame = useRef<HTMLDivElement>(null);
    //#region scene one Refs
    const scene1OpeningAniHolder = useRef<HTMLDivElement>(null);
    const scene1Act1AniHolder = useRef<HTMLDivElement>(null);
    const scene1Act2AniHolder = useRef<HTMLDivElement>(null);
    const scene1Act3AniHolder = useRef<HTMLDivElement>(null);

    const restart = useRef<HTMLButtonElement>(null);
    const nextScene = useRef<HTMLButtonElement>(null);
    const transitionFrame = useRef<HTMLDivElement>(null);
    const transitionBg = useRef<HTMLDivElement>(null);
    const lottieFlash = useRef<HTMLDivElement>(null);
    const ctaArrow = useRef<HTMLDivElement>(null);
    const desk = useRef<HTMLDivElement>(null);
    const book = useRef<HTMLImageElement>(null);
    const clock = useRef<HTMLImageElement>(null);
    const lamp = useRef<HTMLImageElement>(null);
    const mug = useRef<HTMLImageElement>(null);
    const plant = useRef<HTMLImageElement>(null);
    const pen = useRef<HTMLImageElement>(null);
    const heading = useRef<HTMLHeadingElement>(null);
    const ctalink = useRef<HTMLAnchorElement>(null);
    const scene1Nav = useRef<HTMLDivElement>(null);
    const scene1Act1 = useRef<HTMLElement>(null);
    const scene1Act2 = useRef<HTMLElement>(null);
    const scene1Act3 = useRef<HTMLElement>(null);
    const scene1Act1Link = useRef<HTMLAnchorElement>(null);
    const scene1Act2Link = useRef<HTMLAnchorElement>(null);
    const scene1Act3Link = useRef<HTMLAnchorElement>(null);
    const scene1Act1Progress = useRef<SVGCircleElement>(null);
    const scene1Act2Progress = useRef<SVGCircleElement>(null);
    const scene1Act3Progress = useRef<SVGCircleElement>(null);
    const scene1LoadProgress = useRef<SVGCircleElement>(null);
    const ctaloader = useRef<HTMLDivElement>(null);

    //#endregion

    //#region scene two Refs
    const scene2OpeningAniHolder = useRef<HTMLDivElement>(null);
    const scene2Act1AniHolder = useRef<HTMLDivElement>(null);
    const scene2Act2AniHolder = useRef<HTMLDivElement>(null);
    const scene2Act3AniHolder = useRef<HTMLDivElement>(null);

    const headphones = useRef<HTMLImageElement>(null);
    const notebook = useRef<HTMLImageElement>(null);
    const pens = useRef<HTMLImageElement>(null);
    const tablet = useRef<HTMLImageElement>(null);
    const tray = useRef<HTMLImageElement>(null);
    const watch = useRef<HTMLImageElement>(null);

    const scene2bg = useRef<HTMLDivElement>(null);
    const scene2Nav = useRef<HTMLDivElement>(null);
    const scene2Act1 = useRef<HTMLElement>(null);
    const scene2Act2 = useRef<HTMLElement>(null);
    const scene2Act3 = useRef<HTMLElement>(null);
    const scene2Act1Link = useRef<HTMLAnchorElement>(null);
    const scene2Act2Link = useRef<HTMLAnchorElement>(null);
    const scene2Act3Link = useRef<HTMLAnchorElement>(null);
    const scene2Act1Progress = useRef<SVGCircleElement>(null);
    const scene2Act2Progress = useRef<SVGCircleElement>(null);
    const scene2Act3Progress = useRef<SVGCircleElement>(null);


    const scene3OpeningAniHolder = useRef<HTMLDivElement>(null);
    const scene3Act1AniHolder = useRef<HTMLDivElement>(null);
    const scene3Act2AniHolder = useRef<HTMLDivElement>(null);
    const scene3Act3AniHolder = useRef<HTMLDivElement>(null);
    const scene3ClosingAniHolder = useRef<HTMLDivElement>(null);

    const egg = useRef<HTMLImageElement>(null);
    const lightshade = useRef<HTMLImageElement>(null);
    const mobile = useRef<HTMLImageElement>(null);
    const mugcafe = useRef<HTMLImageElement>(null);
    const playButton = useRef<HTMLImageElement>(null);
    const pauseButton = useRef<HTMLImageElement>(null);
    const playPauseTrigger = useRef<HTMLDivElement>(null);
    const pegboard = useRef<HTMLImageElement>(null);
    const sugarcube = useRef<HTMLImageElement>(null);
    const teapot = useRef<HTMLImageElement>(null);
    const tablenumber = useRef<HTMLImageElement>(null);
    const frame1 = useRef<HTMLImageElement>(null);
    const frame2 = useRef<HTMLImageElement>(null);
    const frame3 = useRef<HTMLImageElement>(null);
    const laptop = useRef<HTMLImageElement>(null);
    const icons = useRef<HTMLImageElement>(null);
    const deskAct3 = useRef<HTMLDivElement>(null);
    const getInTouch = useRef<HTMLAnchorElement>(null);
    const finalCopy = useRef<HTMLHeadingElement>(null);

    const scene3Nav = useRef<HTMLDivElement>(null);
    const scene3Act1 = useRef<HTMLElement>(null);
    const scene3Act2 = useRef<HTMLElement>(null);
    const scene3Act3 = useRef<HTMLElement>(null);
    const scene3Act1Link = useRef<HTMLAnchorElement>(null);
    const scene3Act2Link = useRef<HTMLAnchorElement>(null);
    const scene3Act3Link = useRef<HTMLAnchorElement>(null);
    const scene3Act1Progress = useRef<SVGCircleElement>(null);
    const scene3Act2Progress = useRef<SVGCircleElement>(null);
    const scene3Act3Progress = useRef<SVGCircleElement>(null);

    //#endregion
    const loadedAniList: { name: string; loaded: AnimationItem; nested: (animation: AnimationItem) => gsap.core.Timeline; }[] = useMemo(() => { return [] }, []);
    const removeAniList: { name: string; loaded: AnimationItem; nested: gsap.core.Timeline; }[] = useMemo(() => { return [] }, []);

    useEffect(() => {
        if (dontTrack) {
            setDontTrack(false);
        } else {
            sendPostMessage("Google workspace demo: interaction", message, "autoplay");
        }
    }, [message, dontTrack]);



    const aniList = useMemo(() => {
        return [{
            name: "scene1OpeningAni",
            scene: "connect",
            container: scene1OpeningAniHolder,
            path: "./assets/scene1/01_Opening/data.json",
            nested: (animation: AnimationItem) => {
                let duration = 1 * animation.totalFrames / animation.frameRate;

                let playhead = { frame: 0 }
                let nested = gsap.timeline({ useFrames: false, paused: false, repeat: 0, ease: "none" });
                //#region 

                nested.fromTo([ctaloader.current], {
                    opacity: 0
                }, {
                    opacity: 0,
                    duration: 0.1
                }, 0);

                nested.fromTo([playPauseTrigger.current], {
                    opacity: 0, display: "none"
                }, {
                    opacity: 0, display: "none",
                    duration: 0.1
                }, 0);
                nested.fromTo(getInTouch.current, {
                    top: 4000
                }, {
                    top: 4000,
                    duration: duration
                }, 0);
                nested.fromTo([playPauseTrigger.current, lottieFlash.current], {
                    left: -337,
                    top: -190,
                    width: 616,
                    height: 389,
                }, {
                    left: -337,
                    top: -190,
                    width: 616,
                    height: 389,
                    duration: 0.1
                }, 0);
                nested.fromTo([scene1Nav.current, transitionFrame.current, transitionBg.current], {
                    opacity: 0
                }, {
                    opacity: 0,
                    duration: 0.1,
                    onStart: () => {
                        setScene("connect");
                    }
                }, 0);

                nested.fromTo([plant.current, book.current, clock.current, mug.current, lamp.current, pen.current, desk.current], {
                    opacity: 1
                }, {
                    opacity: 1,
                    duration: 0
                }, 0);

                nested.fromTo([tablet.current, tray.current, watch.current, notebook.current, pens.current, headphones.current], {
                    opacity: 0
                }, {
                    opacity: 0,
                    onStart: () => {
                        if (scene2Act1AniHolder.current) {
                            scene2Act1AniHolder.current.className = "videoPart act2Vid";
                        }
                    },
                    duration: 0.1
                }, 0);

                nested.fromTo(scene1OpeningAniHolder.current, {
                    width: 649,
                    top: -35,
                    left: -364,
                    opacity: 1
                }, {
                    width: 649,
                    top: -35,
                    left: -364,
                    opacity: 1,

                    duration: .1
                }, 0)

                nested.fromTo(gwsDemoFrame.current, {
                    top: 0
                }, {
                    top: 0,
                    duration: duration / 2
                }, 0);


                nested.fromTo([playPauseTrigger.current, lottieFlash.current], {
                    opacity: 0
                }, {
                    opacity: 0,
                    duration: duration
                }, 0);
                nested.fromTo(ctalink.current, {
                    top: 110,
                    alpha: 1,
                }, {
                    ease: "sine.out",
                    top: 60,
                    alpha: 0,
                    duration: duration / 8
                }, 0);
                nested.fromTo(ctalink.current, {
                    zIndex: 111
                }, {
                    zIndex: 5,
                    duration: 0.5
                }, 0);
                nested.fromTo(heading.current, {
                    top: -187,
                    alpha: 1,
                    duration: duration / 2
                }, {
                    ease: "sine.out",
                    top: -287,
                    alpha: 0,
                }, 0);
                nested.fromTo(plant.current, {
                    height: 185,
                    width: 251,
                    top: 106,
                    left: -563
                }, {
                    height: 236,
                    width: 311,
                    top: 18,
                    left: -777,
                    duration: duration
                }, 0);
                nested.fromTo(book.current, {
                    width: 146,
                    top: 284,
                    left: -609
                }, {
                    width: 212,
                    top: 248,
                    left: -823,
                    duration: duration
                }, 0);
                nested.fromTo(clock.current, {
                    width: 261,
                    top: -273,
                    left: -670
                }, {
                    width: 338,
                    top: -413,
                    left: -840,
                    duration: duration
                }, 0);
                nested.fromTo(mug.current, {
                    width: 168,
                    top: 218,
                    left: 253
                }, {
                    width: 218,
                    top: 158,
                    left: 310,
                    duration: duration
                }, 0);
                nested.fromTo(lamp.current, {
                    height: 463,
                    top: -175,
                    left: 311
                }, {
                    height: 612,
                    top: -369,
                    left: 365,
                    duration: duration
                }, 0);
                nested.fromTo(pen.current, {
                    width: 140,
                    top: 115,
                    left: -761
                }, {
                    width: 190,
                    top: 7,
                    left: -967,
                    duration: duration
                }, 0);
                nested.fromTo(desk.current, {
                    top: 244
                }, {
                    top: 173,
                    duration: duration
                }, 0);
                nested.fromTo(scene1OpeningAniHolder.current, {
                    width: 649,
                    left: -364,
                    top: -35
                }, {
                    width: 919,
                    left: -574,
                    top: -208,
                    duration: duration
                }, 0);
                nested.fromTo(playhead, {
                    frame: 0
                }, {
                    frame: animation.totalFrames - 1,
                    duration: duration,
                    ease: `steps(${animation.totalFrames - 1})`,
                    onUpdate: () => {
                        animation.goToAndStop(playhead.frame, true);
                    }
                }, 0);
                nested.fromTo(scene1Nav.current, {
                    alpha: 0,
                    top: 9
                }, {
                    alpha: 1,
                    top: -91,
                    duration: duration / 2
                }, duration / 2);
                nested.fromTo(scene1Act2.current, {
                    alpha: 0,
                    top: 20
                }, {
                    alpha: 1,
                    top: 0,
                    duration: duration / 10
                }, duration * 6 / 10);
                nested.fromTo(scene1Act3.current, {
                    alpha: 0,
                    top: 20
                }, {
                    alpha: 1,
                    top: 0,
                    duration: duration / 10
                }, duration * 7 / 10);
                nested.fromTo([scene1Act1Progress.current, scene1Act2Progress.current, scene1Act3Progress.current], {
                    drawSVG: "0%"
                }, {
                    drawSVG: "0%",
                    duration: duration
                }, 0);
                //#endregion
                return nested;
            }
        },
        {
            name: "scene1Act1Ani",
            scene: "connect",
            container: scene1Act1AniHolder,
            path: "./assets/scene1/Part1/Act1Part1.json",
            nested: (animation: AnimationItem) => {
                let duration = animation.totalFrames / animation.frameRate;
                let playhead = { frame: 0 };
                let nested = gsap.timeline({
                    useFrames: false, paused: false, repeat: 0, ease: "none", onStart: () => {

                        setMessage("Connect with your team : Connect distributed teams");
                    }
                });
                //#region 


                nested.fromTo([playPauseTrigger.current], {
                    opacity: 0,
                    display: "none",
                }, {
                    opacity: 1,
                    display: "block",
                    duration: 0.1
                }, 0);
                nested.fromTo([playPauseTrigger.current, lottieFlash.current], {
                    left: -337,
                    top: -190,
                    width: 616,
                    height: 389,
                }, {
                    left: -337,
                    top: -190,
                    width: 616,
                    height: 389,
                    duration: 0.1
                }, 0);
                nested.fromTo(scene1OpeningAniHolder.current, {
                    width: 919,
                    left: -574,
                    top: -208,
                }, {
                    width: 919,
                    left: -574,
                    top: -208,
                    duration: 0.1
                }, 0);
                nested.fromTo(scene1Act1Progress.current, {
                    drawSVG: "0%"
                }, {
                    drawSVG: "100%",
                    duration: duration
                }, 0);
                nested.fromTo(playhead, {
                    frame: 0
                }, {
                    frame: animation.totalFrames - 1,
                    duration: duration,
                    ease: `steps(${animation.totalFrames - 1})`,
                    onUpdate: () => {
                        animation.goToAndStop(playhead.frame, true);
                    },
                    onStart: () => {
                        if (scene1Act1AniHolder.current && scene1Act1Link.current) {
                            scene1Act1AniHolder.current.className = "videoPart visible";
                            scene1Act1Link.current.className = "act1 active";
                            setAct("act1");
                        }
                        if (scene1Act2AniHolder.current && scene1Act2Link.current) {
                            scene1Act2AniHolder.current.className = "videoPart";
                            scene1Act2Link.current.className = "act2";
                        }
                        if (scene1Act3AniHolder.current && scene1Act3Link.current) {
                            scene1Act3AniHolder.current.className = "videoPart";
                            scene1Act3Link.current.className = "act3";
                        }

                    },
                    onReverseComplete: () => {
                        if (scene1Act1AniHolder.current && scene1Act1Link.current) {
                            scene1Act1AniHolder.current.className = "videoPart";
                            scene1Act1Link.current.className = "act1 active";
                            setAct("act1");
                        }
                        if (scene1Act2AniHolder.current && scene1Act2Link.current) {
                            scene1Act2AniHolder.current.className = "videoPart";
                            scene1Act2Link.current.className = "act2";
                        }
                        if (scene1Act3AniHolder.current && scene1Act3Link.current) {
                            scene1Act3AniHolder.current.className = "videoPart";
                            scene1Act3Link.current.className = "act3";
                        }

                    }
                }, 0);
                //#endregion
                return nested;
            }
        },
        {
            name: "scene1Act2Ani",
            scene: "connect",
            container: scene1Act2AniHolder,
            path: "./assets/scene1/Part2/Act1Part2.json",
            nested: (animation: AnimationItem) => {
                let duration = 1 * animation.totalFrames / animation.frameRate;
                let playhead = { frame: 0 };
                let nested = gsap.timeline({
                    useFrames: false, paused: false, repeat: 0, ease: "none", onStart: () => {

                        setMessage("Connect with your team : Empower everyone to contribute");
                    }
                });
                //#region 
                nested.fromTo(scene1Act2Progress.current, {
                    drawSVG: "0%"
                }, {
                    drawSVG: "100%",
                    duration: duration
                }, 0);
                nested.fromTo(playhead, {
                    frame: 0
                }, {
                    frame: animation.totalFrames - 1,
                    duration: duration,
                    ease: `steps(${animation.totalFrames - 1})`,
                    onUpdate: () => {
                        animation.goToAndStop(playhead.frame, true);
                    },
                    onStart: () => {

                        if (scene1Act2AniHolder.current && scene1Act2Link.current) {
                            scene1Act2AniHolder.current.className = "videoPart visible";
                            setAct("act2");
                            scene1Act2Link.current.className = "act2 active";
                        }

                        if (scene1Act1AniHolder.current && scene1Act1Link.current) {
                            scene1Act1AniHolder.current.className = "videoPart";
                            scene1Act1Link.current.className = "act1";
                        }
                        if (scene1Act3AniHolder.current && scene1Act3Link.current) {
                            scene1Act3AniHolder.current.className = "videoPart";
                            scene1Act3Link.current.className = "act3";
                        }
                    },
                    onReverseComplete: () => {
                        if (scene1Act1AniHolder.current && scene1Act1Link.current) {
                            scene1Act1AniHolder.current.className = "videoPart visible";
                            scene1Act1Link.current.className = "act1 active";
                            setAct("act1");
                        }
                        if (scene1Act2AniHolder.current && scene1Act2Link.current) {
                            scene1Act2AniHolder.current.className = "videoPart";
                            scene1Act2Link.current.className = "act2";
                        }
                        if (scene1Act3AniHolder.current && scene1Act3Link.current) {
                            scene1Act3AniHolder.current.className = "videoPart";
                            scene1Act3Link.current.className = "act3";
                        }
                    }
                }, 0);
                //#endregion
                return nested;
            }
        },
        {
            name: "scene1Act3Ani",
            scene: "connect",
            container: scene1Act3AniHolder,
            path: "./assets/scene1/Part3/Act1Part3.json",
            nested: (animation: AnimationItem) => {
                let duration = 1 * animation.totalFrames / animation.frameRate;
                let playhead = { frame: 0 };
                let nested = gsap.timeline({
                    useFrames: false, paused: false, repeat: 0, ease: "none", onStart: () => {

                        setMessage("Connect with your team : Get on the same page");
                    }
                });
                //#region 
                nested.fromTo(scene1Act3Progress.current, {
                    drawSVG: "0%"
                }, {
                    drawSVG: "100%",
                    duration: duration,
                    onComplete: () => {
                        if (animations.scroller) {
                            animations.scroller.kill();
                            setTimeout(() => {

                                gsap.fromTo(nextScene.current, {
                                    bottom: "-90px"
                                }, {
                                    bottom: "-25px",
                                    opacity: 1,
                                    duration: 0.25
                                });
                            }, 50)

                            setAnimations(animations);
                        }
                    }
                }, 0);
                nested.fromTo([playPauseTrigger.current], {
                    opacity: 1,
                    display: "block",
                }, {
                    opacity: 0,
                    display: "none",
                    duration: 0.1
                }, duration - 0.1);
                nested.fromTo(playhead, {
                    frame: 0
                }, {
                    frame: animation.totalFrames - 1,
                    duration: duration,
                    ease: `steps(${animation.totalFrames - 1})`,
                    onUpdate: () => {
                        animation.goToAndStop(playhead.frame, true);
                    },
                    onStart: () => {
                        if (scene1Act1AniHolder.current && scene1Act1Link.current) {
                            scene1Act1AniHolder.current.className = "videoPart";
                            scene1Act1Link.current.className = "act1";
                        }
                        if (scene1Act2AniHolder.current && scene1Act2Link.current) {
                            scene1Act2AniHolder.current.className = "videoPart";
                            scene1Act2Link.current.className = "act2";

                        }
                        if (scene1Act3AniHolder.current && scene1Act3Link.current) {
                            scene1Act3AniHolder.current.className = "videoPart visible";
                            scene1Act3Link.current.className = "act3 active";
                            setAct("act3");
                        }
                    },
                    onReverseComplete: () => {
                        if (scene1Act1AniHolder.current && scene1Act1Link.current) {
                            scene1Act1AniHolder.current.className = "videoPart";
                            scene1Act1Link.current.className = "act1";
                        }
                        if (scene1Act2AniHolder.current && scene1Act2Link.current) {
                            scene1Act2AniHolder.current.className = "videoPart visible";
                            scene1Act2Link.current.className = "act2 active";
                            setAct("act2");
                        }
                        if (scene1Act3AniHolder.current && scene1Act3Link.current) {
                            scene1Act3AniHolder.current.className = "videoPart";
                            scene1Act3Link.current.className = "act3";
                        }
                    }
                }, 0);
                //#endregion
                return nested;
            }
        },
        {
            name: "scene2OpeningAni",
            scene: "create",
            container: scene2OpeningAniHolder,
            path: "./assets/scene2/01_Opening/data.json",
            nested: (animation: AnimationItem) => {
                let duration = 1 * animation.totalFrames / animation.frameRate;
                let playhead = { frame: 0 };
                let nested = gsap.timeline({ useFrames: false, paused: false, repeat: 0, ease: "power4.inOut" });
                //#region 


                nested.fromTo(gwsDemoFrame.current, {
                    top: 0
                }, {
                    top: -685,
                    duration: duration / 2
                }, duration / 8);

                nested.fromTo(transitionFrame.current, {
                    opacity: 0
                }, {
                    opacity: 1,
                    duration: 0.1
                }, 0);

                nested.fromTo(transitionFrame.current, {
                    borderWidth: 1
                }, {
                    borderWidth: 30,
                    duration: duration / 2
                }, 0);
                nested.fromTo(transitionFrame.current, {
                    borderWidth: 30
                }, {
                    borderWidth: 1,
                    duration: duration / 2
                }, duration - duration / 2);

                nested.fromTo(transitionFrame.current, {
                    opacity: 1
                }, {
                    opacity: 0,
                    duration: 0.1
                }, (duration) - 0.1);
                nested.fromTo(transitionBg.current, {
                    opacity: 0
                }, {
                    opacity: 1,
                    duration: 0.1
                }, 0);

                nested.fromTo(transitionBg.current, {
                    backgroundColor: 'rgba(255, 255, 255, 0.01)'
                }, {
                    backgroundColor: 'rgba(255, 255, 255, 0.75)',
                    duration: duration / 2
                }, 0);
                nested.fromTo(transitionBg.current, {
                    backgroundColor: 'rgba(255, 255, 255, 0.75)'
                }, {
                    backgroundColor: 'rgba(255, 255, 255, 0.01)',
                    duration: duration / 2
                }, duration - duration / 2);

                nested.fromTo(transitionBg.current, {
                    opacity: 1
                }, {
                    opacity: 0,
                    duration: 0.1
                }, (duration) - 0.1);


                nested.fromTo([scene1OpeningAniHolder.current, scene1Act3AniHolder.current, plant.current, book.current, clock.current, mug.current, lamp.current, pen.current, desk.current], {
                    opacity: 1
                }, {
                    opacity: 0,
                    onComplete: () => {
                        if (scene1Act3AniHolder.current) {
                            scene1Act3AniHolder.current.className = "videoPart";
                            scene1Act3AniHolder.current.style.opacity = "";
                        }
                    },
                    onReverseComplete: () => {
                        if (scene1Act3AniHolder.current) {
                            scene1Act3AniHolder.current.className = "videoPart visible";
                            scene1Act3AniHolder.current.style.opacity = "";
                        }
                    },
                    duration: duration / 4
                }, duration / 4);

                nested.fromTo([playPauseTrigger.current, scene2Act1AniHolder.current, tablet.current, tray.current, watch.current, notebook.current, pens.current, headphones.current], {
                    opacity: 0,
                    display: "none",
                }, {
                    opacity: 1,
                    display: "block",
                    onComplete: () => {
                        if (scene2Act1AniHolder.current) {
                            scene2Act1AniHolder.current.className = "videoPart act2Vid visible";
                            scene2Act1AniHolder.current.style.opacity = "";
                        }
                    },
                    onReverseComplete: () => {
                        if (scene2Act1AniHolder.current) {
                            scene2Act1AniHolder.current.className = "videoPart visible";
                            scene2Act1AniHolder.current.style.opacity = "";
                        }
                    },
                    duration: duration / 4
                }, duration / 4);

                nested.fromTo(headphones.current, {
                    width: 350,
                    top: 502,
                    left: -824
                }, {
                    width: 407,
                    top: 202,
                    left: -824,
                    duration: duration / 2
                }, 0.5);
                nested.fromTo(pens.current, {
                    width: 117,
                    top: 1132,
                    left: -528
                }, {
                    width: 131,
                    top: 832,
                    left: -528,
                    duration: duration / 2
                }, 0.5);

                nested.fromTo(notebook.current, {
                    width: 335,
                    top: 891,
                    left: -856
                }, {
                    width: 385,
                    left: -856,
                    top: 591,
                    duration: duration / 2
                }, 0.5);
                nested.fromTo(watch.current, {
                    width: 220,
                    top: 528,
                    left: 388
                }, {
                    width: 255,
                    top: 228,
                    left: 388,
                    duration: duration / 2
                }, 0.5);
                nested.fromTo(tray.current, {
                    width: 196,
                    top: 1149,
                    left: 405
                }, {
                    width: 226,
                    top: 849,
                    left: 405,
                    duration: duration / 2
                }, 0.5);
                nested.fromTo(tablet.current, {
                    width: 654,
                    top: 779,
                    left: -368
                }, {
                    width: 654,
                    top: 479,
                    left: -368,
                    duration: duration / 2
                }, 0.5);
                nested.fromTo(scene2Act1AniHolder.current, {
                    top: 764,
                }, {
                    top: 494,
                    duration: duration / 2
                }, 0.5)
                nested.fromTo(plant.current, {
                    top: 17,
                    left: -777
                }, {
                    top: -183,
                    left: -777,
                    duration: duration / 2
                }, 0.5);
                nested.fromTo(book.current, {
                    width: 212,
                    top: 248,
                    left: -823
                }, {
                    top: 48,
                    left: -823,
                    duration: duration / 2
                }, 0.5);
                nested.fromTo(clock.current, {
                    top: -413,
                    left: -840
                }, {
                    left: -840,
                    top: -613,
                    duration: duration / 2
                }, 0.5);
                nested.fromTo(mug.current, {
                    top: 158,
                    left: 310
                }, {
                    top: -42,
                    left: 310,
                    duration: duration / 2
                }, 0.5);
                nested.fromTo(lamp.current, {
                    top: -369,
                    left: 365
                }, {
                    left: 365,
                    top: -569,
                    duration: duration / 2
                }, 0.5);
                nested.fromTo(pen.current, {
                    top: 15,
                    left: -967
                }, {
                    left: -967,
                    top: -300,
                    duration: duration / 2
                }, 0.5);
                nested.fromTo(desk.current, {
                    top: 173
                }, {
                    top: -17,
                    duration: duration / 2,
                    onStart: () => {
                        setAct("act1");
                        setScene("create");
                    },
                    onReverseComplete: () => {
                        setScene("connect");
                        setAct("act3");
                    }
                }, 0.5);
                nested.fromTo(playhead, {
                    frame: 0
                }, {
                    frame: animation.totalFrames - 1,
                    duration: duration,
                    ease: `steps(${animation.totalFrames - 1})`,
                    onUpdate: () => {
                        animation.goToAndStop(playhead.frame, true);
                    },
                    onStart: () => {
                        if (scene2OpeningAniHolder.current && scene2Act1AniHolder.current) {
                            scene2OpeningAniHolder.current.className = "videoPart act2Opening visible";
                            scene2Act1AniHolder.current.className = "videoPart act2Vid visible";
                        }

                    },
                    onReverseComplete: () => {
                        if (scene2OpeningAniHolder.current && scene2Act1AniHolder.current) {
                            scene2OpeningAniHolder.current.className = "videoPart act2Opening";
                            scene2Act1AniHolder.current.className = "videoPart act2Vid";
                        }
                    }
                }, 0);
                nested.fromTo(scene1Nav.current, {
                    alpha: 1
                }, {
                    alpha: 0,
                    duration: duration / 2
                }, 0);
                nested.fromTo(scene2Nav.current, {
                    alpha: 0,
                    top: 693
                }, {
                    alpha: 1,
                    top: 593,
                    duration: duration / 2
                }, duration / 2);
                nested.fromTo(scene2Act2.current, {
                    alpha: 0,
                    top: 20
                }, {
                    alpha: 1,
                    top: 0,
                    duration: duration / 10
                }, duration * 6 / 10);
                nested.fromTo(scene2Act3.current, {
                    alpha: 0,
                    top: 20
                }, {
                    alpha: 1,
                    top: 0,
                    duration: duration / 10
                }, duration * 7 / 10);
                nested.fromTo([scene2Act1Progress.current, scene2Act2Progress.current, scene2Act3Progress.current], {
                    drawSVG: "0%"
                }, {
                    drawSVG: "0%",
                    duration: duration
                }, 0);


                //#endregion
                return nested;
            }
        },
        {
            name: "scene2Act1Ani",
            scene: "create",
            container: scene2Act1AniHolder,
            path: "./assets/scene2/Part1/Act2Part1.json",
            nested: (animation: AnimationItem) => {
                let duration = 1 * animation.totalFrames / animation.frameRate;
                let playhead = { frame: 0 };
                let nested = gsap.timeline({
                    useFrames: false, paused: false, repeat: 0, ease: "none", onStart: () => {

                        setMessage("Create in real time: Share rich content remotely");
                    }
                });
                //#region 


                nested.fromTo([playPauseTrigger.current], {
                    opacity: 0,
                    display: "none",
                }, {
                    opacity: 1,
                    display: "block",
                    duration: 0.1
                }, 0);
                nested.fromTo([lottieFlash.current, playPauseTrigger.current], {
                    left: -337,
                    top: -190,
                    width: 616,
                    height: 389
                }, {
                    left: -337,
                    top: -190,
                    width: 616,
                    height: 389,
                    duration: 0.1
                }, 0);
                nested.fromTo(scene2Act1Progress.current, {
                    drawSVG: "0%"
                }, {
                    drawSVG: "100%",
                    duration: duration
                }, 0);

                nested.fromTo(scene2OpeningAniHolder.current, {
                    opacity: 1
                }, {
                    opacity: 0,
                    onComplete: () => {
                        if (scene2OpeningAniHolder.current) {
                            scene2OpeningAniHolder.current.className = "videoPart act2Opening";
                            scene2OpeningAniHolder.current.style.opacity = "";
                        }
                    },
                    onReverseComplete: () => {
                        if (scene2OpeningAniHolder.current) {
                            scene2OpeningAniHolder.current.className = "videoPart act2Opening visible";
                            scene2OpeningAniHolder.current.style.opacity = "";
                        }
                    },
                    duration: 0.1
                }, 4.15);
                nested.fromTo(playhead, {
                    frame: 0
                }, {
                    frame: animation.totalFrames - 1,
                    duration: duration,
                    ease: `steps(${animation.totalFrames - 1})`,
                    onUpdate: () => {
                        animation.goToAndStop(playhead.frame, true);
                    },
                    onStart: () => {
                        if (scene2Act1AniHolder.current && scene2Act1Link.current) {
                            scene2Act1AniHolder.current.className = "videoPart act2Vid visible";
                            scene2Act1Link.current.className = "act1 active";
                            setAct("act1");
                        }
                        if (scene2Act2AniHolder.current && scene2Act2Link.current) {
                            scene2Act2AniHolder.current.className = "videoPart act2Vid";
                            scene2Act2Link.current.className = "act2";
                        }
                        if (scene2Act3AniHolder.current && scene2Act3Link.current) {
                            scene2Act3AniHolder.current.className = "videoPart act2Vid";
                            scene2Act3Link.current.className = "act3";
                        }

                    },
                    onReverseComplete: () => {
                        if (scene2Act1AniHolder.current && scene2Act1Link.current) {
                            scene2Act1AniHolder.current.className = "videoPart act2Vid visible";
                            scene2Act1Link.current.className = "act1 active";
                            setAct("act1");
                        }
                        if (scene2Act2AniHolder.current && scene2Act2Link.current) {
                            scene2Act2AniHolder.current.className = "videoPart act2Vid";
                            scene2Act2Link.current.className = "act2";
                        }
                        if (scene2Act3AniHolder.current && scene2Act3Link.current) {
                            scene2Act3AniHolder.current.className = "videoPart act2Vid";
                            scene2Act3Link.current.className = "act3";
                        }
                    }
                }, 0);
                //#endregion
                return nested;
            }
        },
        {
            name: "scene2Act2Ani",
            scene: "create",
            container: scene2Act2AniHolder,
            path: "./assets/scene2/Part2/Act2Part2.json",
            nested: (animation: AnimationItem) => {
                let duration = 1 * animation.totalFrames / animation.frameRate;
                let playhead = { frame: 0 };
                let nested = gsap.timeline({
                    useFrames: false, paused: false, repeat: 0, ease: "none", onStart: () => {

                        setMessage("Create in real time: Give everyone a voice");
                    }
                });
                //#region 
                nested.fromTo(scene2Act2Progress.current, {
                    drawSVG: "0%"
                }, {
                    drawSVG: "100%",
                    duration: duration
                }, 0);
                nested.fromTo(playhead, {
                    frame: 0
                }, {
                    frame: animation.totalFrames - 1,
                    duration: duration,
                    ease: `steps(${animation.totalFrames - 1})`,
                    onUpdate: () => {
                        animation.goToAndStop(playhead.frame, true);
                    },
                    onStart: () => {
                        if (scene2Act1AniHolder.current && scene2Act1Link.current) {
                            scene2Act1AniHolder.current.className = "videoPart act2Vid";
                            scene2Act1Link.current.className = "act1";
                        }
                        if (scene2Act2AniHolder.current && scene2Act2Link.current) {
                            scene2Act2AniHolder.current.className = "videoPart act2Vid visible";
                            scene2Act2Link.current.className = "act2 active";
                            setAct("act2");
                        }
                        if (scene2Act3AniHolder.current && scene2Act3Link.current) {
                            scene2Act3AniHolder.current.className = "videoPart act2Vid";
                            scene2Act3Link.current.className = "act3";
                        }
                    },
                    onReverseComplete: () => {
                        if (scene2Act1AniHolder.current && scene2Act1Link.current) {
                            scene2Act1AniHolder.current.className = "videoPart act2Vid visible";
                            scene2Act1Link.current.className = "act1 active";
                            setAct("act1");
                        }
                        if (scene2Act2AniHolder.current && scene2Act2Link.current) {
                            scene2Act2AniHolder.current.className = "videoPart act2Vid";
                            scene2Act2Link.current.className = "act2";
                        }
                        if (scene2Act3AniHolder.current && scene2Act3Link.current) {
                            scene2Act3AniHolder.current.className = "videoPart act2Vid";
                            scene2Act3Link.current.className = "act3";
                        }
                    }
                }, 0);
                //#endregion
                return nested;
            }
        },
        {
            name: "scene2Act3Ani",
            scene: "create",
            container: scene2Act3AniHolder,
            path: "./assets/scene2/Part3/Act2Part3.json",
            nested: (animation: AnimationItem) => {
                let duration = 1 * animation.totalFrames / animation.frameRate;
                let playhead = { frame: 0 };
                let nested = gsap.timeline({
                    useFrames: false, paused: false, repeat: 0, ease: "none", onStart: () => {


                        setMessage("Create in real time : Organize next steps");
                    }
                });
                //#region 
                nested.fromTo([playPauseTrigger.current], {
                    opacity: 1,
                    display: "block",
                }, {
                    opacity: 0,
                    display: "none",
                    duration: 0.1
                }, duration - 0.1);
                nested.fromTo(scene2Act3Progress.current, {
                    drawSVG: "0%"
                }, {
                    drawSVG: "100%",
                    duration: duration,
                    onComplete: () => {
                        if (animations.scroller) {
                            animations.scroller.kill();
                            setTimeout(() => {

                                gsap.fromTo(nextScene.current, {
                                    bottom: "-90px"
                                }, {
                                    bottom: "-25px",
                                    opacity: 1,
                                    duration: 0.25
                                });
                            }, 50)

                            setAnimations(animations);
                        }
                    }
                }, 0);

                nested.fromTo(playhead, {
                    frame: 0
                }, {
                    frame: animation.totalFrames - 1,
                    duration: duration,
                    ease: `steps(${animation.totalFrames - 1})`,
                    onUpdate: () => {
                        animation.goToAndStop(playhead.frame, true);
                    },
                    onStart: () => {
                        if (scene2Act1AniHolder.current && scene2Act1Link.current) {
                            scene2Act1AniHolder.current.className = "videoPart act2Vid";
                            scene2Act1Link.current.className = "act1";
                        }
                        if (scene2Act2AniHolder.current && scene2Act2Link.current) {
                            scene2Act2AniHolder.current.className = "videoPart act2Vid";
                            scene2Act2Link.current.className = "act2";
                        }
                        if (scene2Act3AniHolder.current && scene2Act3Link.current) {
                            scene2Act3AniHolder.current.className = "videoPart act2Vid visible";
                            scene2Act3Link.current.className = "act3 active";
                            setAct("act3");
                        }
                    },
                    onReverseComplete: () => {
                        if (scene2Act1AniHolder.current && scene2Act1Link.current) {
                            scene2Act1AniHolder.current.className = "videoPart act2Vid";
                            scene2Act1Link.current.className = "act1";
                        }
                        if (scene2Act2AniHolder.current && scene2Act2Link.current) {
                            scene2Act2AniHolder.current.className = "videoPart act2Vid visible";
                            scene2Act2Link.current.className = "act2 active";
                            setAct("act2");
                        }
                        if (scene2Act3AniHolder.current && scene2Act3Link.current) {
                            scene2Act3AniHolder.current.className = "videoPart act2Vid";
                            scene2Act3Link.current.className = "act3";
                        }
                    }
                }, 0);
                //#endregion
                return nested;
            }
        },
        {
            name: "scene3OpeningAni",
            scene: "create",
            container: scene3OpeningAniHolder,
            path: "./assets/scene3/Opening/data.json",
            nested: (animation: AnimationItem) => {
                let duration = 1 * animation.totalFrames / animation.frameRate;

                let playhead = { frame: 0 };
                let nested = gsap.timeline({ useFrames: false, paused: false, repeat: 0, ease: "power4.inOut" });
                //#region 

                nested.fromTo(gwsDemoFrame.current, {
                    top: -685
                }, {
                    top: -1504,
                    duration: duration / 2
                }, duration / 4);

                nested.fromTo(transitionFrame.current, {
                    opacity: 0
                }, {
                    opacity: 1,
                    duration: 0.1
                }, 0);

                nested.fromTo(transitionFrame.current, {
                    borderWidth: 1
                }, {
                    borderWidth: 30,
                    duration: duration / 2
                }, 0);
                nested.fromTo(transitionFrame.current, {
                    borderWidth: 30
                }, {
                    borderWidth: 1,
                    duration: duration / 2
                }, duration - duration / 2);

                nested.fromTo(transitionFrame.current, {
                    opacity: 1
                }, {
                    opacity: 0,
                    duration: 0.1
                }, (duration) - 0.1);
                nested.fromTo(transitionBg.current, {
                    opacity: 0
                }, {
                    opacity: 1,
                    duration: 0.1
                }, 0);

                nested.fromTo(transitionBg.current, {
                    backgroundColor: 'rgba(255, 255, 255, 0.01)'
                }, {
                    backgroundColor: 'rgba(255, 255, 255, 0.75)',
                    duration: duration / 2
                }, 0);
                nested.fromTo(transitionBg.current, {
                    backgroundColor: 'rgba(255, 255, 255, 0.75)'
                }, {
                    backgroundColor: 'rgba(255, 255, 255, 0.01)',
                    duration: duration / 2
                }, duration - duration / 2);

                nested.fromTo(transitionBg.current, {
                    opacity: 1
                }, {
                    opacity: 0,
                    duration: 0.1
                }, (duration) - 0.1);

                nested.fromTo([scene2Act3AniHolder.current, tablet.current, tray.current, watch.current, notebook.current, pens.current, headphones.current], {
                    opacity: 1
                }, {
                    opacity: 0,
                    onComplete: () => {
                        if (scene2Act3AniHolder.current) {
                            scene2Act3AniHolder.current.className = "videoPart act2Vid";
                            scene2Act3AniHolder.current.style.opacity = "";
                        }
                    },
                    onReverseComplete: () => {
                        if (scene2Act3AniHolder.current) {
                            scene2Act3AniHolder.current.className = "videoPart act2Vid visible";
                            scene2Act3AniHolder.current.style.opacity = "";
                        }
                    },
                    duration: duration / 4
                }, duration / 4);

                nested.fromTo([scene3Act1AniHolder.current, tablenumber.current, egg.current, lightshade.current, mobile.current, mugcafe.current, pegboard.current, sugarcube.current, teapot.current], {
                    opacity: 0
                }, {
                    opacity: 1,
                    onComplete: () => {
                        if (scene3Act1AniHolder.current) {
                            scene3Act1AniHolder.current.className = "videoPart act3Vid visible";
                            scene3Act1AniHolder.current.style.opacity = "";
                        }
                    },
                    onReverseComplete: () => {
                        if (scene3Act1AniHolder.current) {
                            scene3Act1AniHolder.current.className = "videoPart act3Vid";
                            scene3Act1AniHolder.current.style.opacity = "";
                        }
                    },
                    duration: duration / 4
                }, duration / 4);




                nested.fromTo([playPauseTrigger.current, lottieFlash.current], {
                    left: -347,
                    top: -191,
                    width: 616,
                    height: 389,
                }, {
                    height: 481,
                    left: -176,
                    width: 264,
                    top: -215,
                    duration: 0.1
                }, 0);

                nested.fromTo(lightshade.current, {
                    width: 426,
                    top: 1577,
                    left: 343
                }, {
                    width: 473,
                    top: 861,
                    left: 407,
                    duration: duration / 2
                }, duration / 4);


                nested.fromTo(egg.current, {
                    width: 244,
                    top: 2601,
                    left: 164
                }, {
                    width: 327,
                    top: 1678,
                    left: 231,
                    duration: duration / 2
                }, duration / 4);


                nested.fromTo(mobile.current, {
                    width: 585,
                    top: 1986,
                    left: -379
                }, {
                    width: 414,
                    left: -304,
                    top: 1264,
                    duration: duration / 2
                }, duration / 4);

                nested.fromTo(mugcafe.current, {
                    width: 223,
                    top: 2346,
                    left: -484
                }, {
                    width: 270,
                    top: 1618,
                    left: -560,
                    duration: duration / 2
                }, duration / 4);

                nested.fromTo(pegboard.current, {
                    top: 1511,
                    width: 507,
                    left: -797
                }, {
                    top: 1019,
                    width: 527,
                    left: -839,
                    duration: duration / 2
                }, duration / 4);


                nested.fromTo(sugarcube.current, {
                    width: 143,
                    top: 2767,
                    left: -588
                }, {
                    width: 147,
                    top: 1745,
                    left: -717,
                    duration: duration / 2
                }, duration / 4);

                nested.fromTo(teapot.current, {
                    width: 436,
                    top: 1783,
                    left: -902
                }, {
                    width: 526,
                    top: 1506,
                    left: -1071,
                    duration: duration / 2
                }, duration / 4);

                nested.fromTo(tablenumber.current, {
                    width: 100,
                    top: 1796,
                    left: 414
                }, {
                    width: 123,
                    top: 1325,
                    left: 505,
                    duration: duration / 2
                }, duration / 4);

                nested.fromTo(deskAct3.current, {
                    top: 1981
                }, {
                    top: 1666,
                    duration: duration / 2
                }, duration / 4);

                nested.fromTo(scene3Act1AniHolder.current, {
                    top: 1976,
                    width: 362,
                    left: -192,
                    height: 655

                }, {
                    top: 1287,
                    width: 264,
                    left: -177,
                    height: 481,
                    duration: duration / 2,
                    onStart: () => {
                        setAct("act1");
                        setScene("collaborate");

                    },
                    onReverseComplete: () => {
                        setScene("create");
                        setAct("act3");
                    }
                }, duration / 4)

                nested.fromTo(playhead, {
                    frame: 0
                }, {
                    frame: animation.totalFrames - 1,
                    duration: duration,
                    ease: `steps(${animation.totalFrames - 1})`,
                    onUpdate: () => {
                        animation.goToAndStop(playhead.frame, true);
                    },
                    onStart: () => {
                        if (scene3OpeningAniHolder.current && scene3Act1AniHolder.current) {
                            scene3OpeningAniHolder.current.className = "videoPart act3Opening visible";
                            scene3Act1AniHolder.current.className = "videoPart act3Vid visible";
                        }

                    },
                    onReverseComplete: () => {
                        if (scene3OpeningAniHolder.current && scene3Act1AniHolder.current) {
                            scene3OpeningAniHolder.current.className = "videoPart act3Opening";
                            scene3Act1AniHolder.current.className = "videoPart act3Vid";
                        }
                    }
                }, 0);
                nested.fromTo(scene2Nav.current, {
                    alpha: 1
                }, {
                    alpha: 0,
                    duration: duration / 2
                }, 0);
                nested.fromTo(scene3Nav.current, {
                    alpha: 0,
                    top: 1512
                }, {
                    alpha: 1,
                    top: 1412,
                    duration: duration / 2
                }, duration / 2);
                nested.fromTo(scene3Act2.current, {
                    alpha: 0,
                    top: 20
                }, {
                    alpha: 1,
                    top: 0,
                    duration: duration / 10
                }, duration * 6 / 10);
                nested.fromTo(scene3Act3.current, {
                    alpha: 0,
                    top: 20
                }, {
                    alpha: 1,
                    top: 0,
                    duration: duration / 10
                }, duration * 7 / 10);
                nested.fromTo([scene3Act1Progress.current, scene3Act2Progress.current, scene3Act3Progress.current], {
                    drawSVG: "0%"
                }, {
                    drawSVG: "0%",
                    duration: duration
                }, 0);


                //#endregion
                return nested;
            }
        },
        {
            name: "scene3Act1Ani",
            scene: "create",
            container: scene3Act1AniHolder,
            path: "./assets/scene3/Part1/Act3Part1.json",
            nested: (animation: AnimationItem) => {
                let duration = 1 * animation.totalFrames / animation.frameRate;
                let playhead = { frame: 0 };
                let nested = gsap.timeline({
                    useFrames: false, paused: false, repeat: 0, ease: "none", onStart: () => {

                        setMessage("Collaborate from anywhere : Stay on task across devices");
                    }
                });
                //#region 

                nested.fromTo([playPauseTrigger.current], {
                    opacity: 0,
                    display: "none",
                }, {
                    opacity: 1,
                    display: "block",
                    duration: 0.1
                }, 0);

                nested.fromTo(scene3Act1Progress.current, {
                    drawSVG: "0%"
                }, {
                    drawSVG: "100%",
                    duration: duration
                }, 0);
                nested.fromTo(scene3OpeningAniHolder.current, {
                    opacity: 1
                }, {
                    opacity: 0,
                    onStart: () => {
                        if (scene3OpeningAniHolder.current) {
                            scene3OpeningAniHolder.current.className = "videoPart act3Opening visible";
                            scene3OpeningAniHolder.current.style.opacity = "";
                        }

                    },
                    onComplete: () => {
                        if (scene3OpeningAniHolder.current) {
                            scene3OpeningAniHolder.current.className = "videoPart act3Opening";
                            scene3OpeningAniHolder.current.style.opacity = "";
                        }
                    },
                    onReverseComplete: () => {
                        if (scene3OpeningAniHolder.current) {
                            scene3OpeningAniHolder.current.className = "videoPart act3Opening visible";
                            scene3OpeningAniHolder.current.style.opacity = "";
                        }
                    },
                    duration: 0.1
                }, 2.2);
                nested.fromTo(playhead, {
                    frame: 0
                }, {
                    frame: animation.totalFrames - 1,
                    duration: duration,
                    ease: `steps(${animation.totalFrames - 1})`,
                    onUpdate: () => {
                        animation.goToAndStop(playhead.frame, true);
                    },
                    onStart: () => {
                        if (scene2OpeningAniHolder.current) {
                            scene2OpeningAniHolder.current.className = "videoPart act2Opening";
                        }
                        if (scene3Act1AniHolder.current && scene3Act1Link.current) {
                            scene3Act1AniHolder.current.className = "videoPart act3Vid visible";
                            scene3Act1Link.current.className = "act1 active";
                            setAct("act1");
                        }
                        if (scene3Act2AniHolder.current && scene3Act2Link.current) {
                            scene3Act2AniHolder.current.className = "videoPart act3Vid";
                            scene3Act2Link.current.className = "act2";
                        }
                        if (scene3Act3AniHolder.current && scene3Act3Link.current) {
                            scene3Act3AniHolder.current.className = "videoPart act3Vid";
                            scene3Act3Link.current.className = "act3";
                        }
                    },
                    onReverseComplete: () => {
                        if (scene3Act1AniHolder.current && scene3Act1Link.current) {
                            scene3Act1AniHolder.current.className = "videoPart act3Vid visible";
                            scene3Act1Link.current.className = "act1 active";
                            setAct("act1");
                        }
                        if (scene3Act2AniHolder.current && scene3Act2Link.current) {
                            scene3Act2AniHolder.current.className = "videoPart act3Vid";
                            scene3Act2Link.current.className = "act2";
                        }
                        if (scene3Act3AniHolder.current && scene3Act3Link.current) {
                            scene3Act3AniHolder.current.className = "videoPart act3Vid";
                            scene3Act3Link.current.className = "act3";
                        }
                        if (scene3OpeningAniHolder.current && scene2OpeningAniHolder.current) {
                            scene3OpeningAniHolder.current.className = "videoPart act3Opening visible";
                            scene2OpeningAniHolder.current.className = "videoPart act2Opening";
                        }
                    }
                }, 0);
                //#endregion
                return nested;
            }
        },
        {
            name: "scene3Act2Ani",
            scene: "create",
            container: scene3Act2AniHolder,
            path: "./assets/scene3/Part2/Act3Part2.json",
            nested: (animation: AnimationItem) => {
                let duration = 1 * animation.totalFrames / animation.frameRate;
                let playhead = { frame: 0 };
                let nested = gsap.timeline({
                    useFrames: false, paused: false, repeat: 0, ease: "none", onStart: () => {

                        setMessage("Collaborate from anywhere : Work with all file types");
                    }
                });
                //#region 
                nested.fromTo(scene3Act2Progress.current, {
                    drawSVG: "0%"
                }, {
                    drawSVG: "100%",
                    duration: duration
                }, 0);
                nested.fromTo(playhead, {
                    frame: 0
                }, {
                    frame: animation.totalFrames - 1,
                    duration: duration,
                    ease: `steps(${animation.totalFrames - 1})`,
                    onUpdate: () => {
                        animation.goToAndStop(playhead.frame, true);
                    },
                    onStart: () => {
                        if (scene3Act1AniHolder.current && scene3Act1Link.current) {
                            scene3Act1AniHolder.current.className = "videoPart act3Vid";
                            scene3Act1Link.current.className = "act1";
                        }
                        if (scene3Act2AniHolder.current && scene3Act2Link.current) {
                            scene3Act2AniHolder.current.className = "videoPart act3Vid visible";
                            scene3Act2Link.current.className = "act2 active";
                            setAct("act2");
                        }
                        if (scene3Act3AniHolder.current && scene3Act3Link.current) {
                            scene3Act3AniHolder.current.className = "videoPart act3Vid";
                            scene3Act3Link.current.className = "act3";
                        }
                    },
                    onReverseComplete: () => {
                        if (scene3Act1AniHolder.current && scene3Act1Link.current) {
                            scene3Act1AniHolder.current.className = "videoPart act3Vid visible";
                            scene3Act1Link.current.className = "act1 active";
                            setAct("act1");
                        }
                        if (scene3Act2AniHolder.current && scene3Act2Link.current) {
                            scene3Act2AniHolder.current.className = "videoPart act3Vid";
                            scene3Act2Link.current.className = "act2";
                        }
                        if (scene3Act3AniHolder.current && scene3Act3Link.current) {
                            scene3Act3AniHolder.current.className = "videoPart act3Vid";
                            scene3Act3Link.current.className = "act3";
                        }
                    }
                }, 0);
                //#endregion
                return nested;
            }
        },
        {
            name: "scene3Act3Ani",
            scene: "create",
            container: scene3Act3AniHolder,
            path: "./assets/scene3/Part3/Act3Part3.json",
            nested: (animation: AnimationItem) => {
                let duration = 1 * animation.totalFrames / animation.frameRate;
                let playhead = { frame: 0 };
                let nested = gsap.timeline({
                    useFrames: false, paused: false, repeat: 0, ease: "none", onStart: () => {

                        setMessage("Collaborate from anywhere : Achieve more together");
                    }
                });
                //#region 
                const startDur = 1.7;
                const start = 0.8;

                nested.fromTo(transitionFrame.current, {
                    opacity: 0
                }, {
                    opacity: 1,
                    duration: 0.1
                }, start);

                nested.fromTo(transitionFrame.current, {
                    borderWidth: 1
                }, {
                    borderWidth: 30,
                    duration: startDur / 2
                }, start);
                nested.fromTo(transitionFrame.current, {
                    borderWidth: 30
                }, {
                    borderWidth: 1,
                    duration: startDur / 2
                }, startDur - startDur / 2);

                nested.fromTo(transitionFrame.current, {
                    opacity: 1
                }, {
                    opacity: 0,
                    duration: 0.1
                }, (startDur) - 0.1);
                nested.fromTo(transitionBg.current, {
                    opacity: 0
                }, {
                    opacity: 1,
                    duration: 0.1
                }, start);

                nested.fromTo(transitionBg.current, {
                    opacity: 1
                }, {
                    opacity: 0,
                    duration: 0.1
                }, (startDur) - 0.1);

                nested.fromTo(scene3Nav.current, {
                    left: 155
                }, {
                    left: 312,
                    duration: startDur / 2
                }, start);


                nested.fromTo([playPauseTrigger.current, lottieFlash.current], {
                    height: 481,
                    left: -176,
                    width: 264,
                    top: -215
                }, {
                    left: -347,
                    top: -190,
                    width: 616,
                    height: 389,
                    duration: startDur / 2
                }, start);



                nested.fromTo(transitionBg.current, {
                    backgroundColor: 'rgba(255, 255, 255, 0.01)'
                }, {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    duration: startDur / 2
                }, start);
                nested.fromTo(transitionBg.current, {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                }, {
                    backgroundColor: 'rgba(255, 255, 255, 0.01)',
                    duration: startDur / 2
                }, start + startDur - startDur / 2);


                nested.fromTo([tablenumber.current, egg.current, lightshade.current, mobile.current, mugcafe.current, pegboard.current, sugarcube.current, teapot.current], {
                    opacity: 1
                }, {
                    opacity: 0,
                    duration: startDur / 2
                }, start);

                nested.fromTo([scene1OpeningAniHolder.current, plant.current, book.current, clock.current, mug.current, lamp.current, pen.current, desk.current], {
                    opacity: 0
                }, {
                    opacity: 1,
                    duration: startDur / 2
                }, start + startDur / 2);


                nested.fromTo([plant.current, book.current, clock.current, mug.current, lamp.current, pen.current, desk.current], {
                    opacity: 1
                }, {
                    opacity: 0,
                    duration: 0
                }, 0.1);

                nested.fromTo([plant.current, book.current, clock.current, mug.current, lamp.current, pen.current, desk.current], {
                    top: "+=0"
                }, {
                    top: "+=1504",
                    duration: .1
                }, .2);

                nested.fromTo(scene1OpeningAniHolder.current, {
                    width: 649,
                    top: -35,
                    left: -364
                }, {
                    top: 1293,
                    left: -582,
                    width: 919,
                    opacity: 0,
                    duration: .1
                }, .1)

                nested.fromTo(plant.current, {
                    height: 236,
                    width: 311,
                    top: 18,
                    left: -777,
                    zIndex: 20
                }, {
                    top: "+=1504",
                    zIndex: 22,
                    opacity: 0,
                    duration: 0.1
                }, 0);
                nested.fromTo(book.current, {

                    width: 212,
                    top: 248,
                    left: -823
                }, {
                    top: "+=1504",
                    opacity: 0,
                    duration: 0.1
                }, 0);
                nested.fromTo(clock.current, {

                    width: 338,
                    top: -413,
                    left: -840
                }, {
                    top: "+=1504",
                    opacity: 0,
                    duration: 0.1
                }, 0);
                nested.fromTo(mug.current, {

                    width: 218,
                    top: 158,
                    left: 310
                }, {
                    top: "+=1504",
                    opacity: 0,
                    duration: 0.1
                }, 0);
                nested.fromTo(lamp.current, {

                    height: 612,
                    top: -369,
                    left: 365,
                }, {
                    top: "+=1504",
                    opacity: 0,
                    duration: 0.1
                }, 0);
                nested.fromTo(pen.current, {

                    width: 190,
                    top: 7,
                    left: -967
                }, {
                    top: "+=1504",
                    opacity: 0,
                    duration: 0.1
                }, 0);
                nested.fromTo(desk.current, {
                    top: 173
                }, {
                    top: "+=1504",
                    opacity: 0,
                    duration: 0.1
                }, 0);

                nested.fromTo(scene3Act3Progress.current, {
                    drawSVG: "0%"
                }, {
                    drawSVG: "100%",
                    duration: duration - (start + startDur)
                }, start + startDur);

                nested.fromTo([playPauseTrigger.current], {
                    opacity: 1,
                    display: "block",
                }, {
                    opacity: 0,
                    display: "none",
                    duration: 0.1
                }, duration - 0.1);

                nested.fromTo(playhead, {
                    frame: 0
                }, {
                    frame: animation.totalFrames - 1,
                    duration: duration,
                    ease: `steps(${animation.totalFrames - 1})`,
                    onUpdate: () => {
                        animation.goToAndStop(playhead.frame, true);
                    },
                    onStart: () => {
                        if (scene3Act1AniHolder.current && scene3Act1Link.current) {
                            scene3Act1AniHolder.current.className = "videoPart act3Vid";
                            scene3Act1Link.current.className = "act1";
                        }
                        if (scene3Act2AniHolder.current && scene3Act2Link.current) {
                            scene3Act2AniHolder.current.className = "videoPart act3Vid";
                            scene3Act2Link.current.className = "act2";
                        }
                        if (scene3Act3AniHolder.current && scene3Act3Link.current) {
                            scene3Act3AniHolder.current.className = "videoPart act3Part3Vid visible";
                            scene3Act3Link.current.className = "act3 active";
                            setAct("act3");
                        }
                    },
                    onReverseComplete: () => {
                        if (scene3Act1AniHolder.current && scene3Act1Link.current) {
                            scene3Act1AniHolder.current.className = "videoPart act3Vid";
                            scene3Act1Link.current.className = "act1";
                        }
                        if (scene3Act2AniHolder.current && scene3Act2Link.current) {
                            scene3Act2AniHolder.current.className = "videoPart act3Vid visible";
                            scene3Act2Link.current.className = "act2 active";
                            setAct("act2");
                        }
                        if (scene3Act3AniHolder.current && scene3Act3Link.current) {
                            scene3Act3AniHolder.current.className = "videoPart act3Vid";
                            scene3Act3Link.current.className = "act3";
                        }
                    }
                }, 0);
                //#endregion
                return nested;
            }
        },
        {
            name: "scene3Act3ClosingAni",
            scene: "create",
            container: scene3ClosingAniHolder,
            path: "./assets/scene3/Closing/data.json",
            nested: (animation: AnimationItem) => {
                let duration = 1 * animation.totalFrames / animation.frameRate;
                let playhead = { frame: 0 };
                let nested = gsap.timeline({
                    useFrames: false, paused: false, repeat: 0, ease: "none", onStart: () => {

                        setMessage("Collaborate from anywhere : Call to action");
                    }
                });
                //#region 
                nested.fromTo(getInTouch.current, {
                    top: 4000
                }, {
                    top: 4000,
                    duration: 0.1
                }, 0);
                nested.fromTo(deskAct3.current, {
                    opacity: 1,
                }, {
                    opacity: 0,
                    duration: 0.1
                }, 0);
                nested.fromTo(plant.current, {
                    height: 236,
                    width: 311,
                    top: 1522,
                    left: -777
                }, {
                    height: 185,
                    width: 251,
                    top: 1610,
                    left: -563,
                    duration: duration
                }, 0);
                nested.fromTo(book.current, {
                    width: 212,
                    top: 1752,
                    left: -823
                }, {
                    width: 146,
                    top: 1788,
                    left: -609,
                    duration: duration
                }, 0);
                nested.fromTo(clock.current, {
                    width: 338,
                    top: 1091,
                    left: -840
                }, {
                    width: 261,
                    top: 1231,
                    left: -670,
                    duration: duration
                }, 0);
                nested.fromTo(mug.current, {
                    width: 218,
                    top: 1662,
                    left: 310
                }, {
                    width: 168,
                    top: 1722,
                    left: 253,
                    duration: duration
                }, 0);
                nested.fromTo(lamp.current, {
                    height: 612,
                    top: 1135,
                    left: 365
                }, {
                    height: 463,
                    top: 1329,
                    left: 311,
                    duration: duration
                }, 0);
                nested.fromTo(pen.current, {
                    width: 190,
                    top: 1511,
                    left: -967
                }, {
                    width: 140,
                    top: 1619,
                    left: -761,
                    duration: duration
                }, 0);
                nested.fromTo(desk.current, {
                    top: 1677
                }, {
                    top: 1748,
                    duration: duration
                }, 0);
                nested.fromTo(scene3ClosingAniHolder.current, {
                    top: 1293,
                    left: -582,
                    width: 919,
                }, {
                    width: 649,
                    left: -364,
                    top: 1394,
                    duration: duration
                }, 0);
                nested.fromTo(scene3Nav.current, {
                    alpha: 1,
                    top: 1412
                }, {
                    alpha: 0,
                    top: 1512,
                    duration: duration / 2
                }, 0);


                nested.fromTo(laptop.current, {
                    opacity: 0
                }, {
                    opacity: 1,
                    duration: 0.1
                }, duration - 0.2);

                nested.fromTo([frame1.current, frame2.current, frame3.current], {
                    opacity: 0
                }, {
                    opacity: 1,
                    duration: duration / 2
                }, 0);
                nested.fromTo(frame1.current, {
                    width: 224,
                    left: -372,
                    top: 1413
                }, {
                    width: 154,
                    left: -258,
                    top: 1545,
                    duration: duration
                }, 0);
                nested.fromTo(frame2.current, {
                    width: 266,
                    left: -142,
                    top: 1450,
                }, {
                    width: 195,
                    left: -82,
                    top: 1572,
                    duration: duration
                }, 0);
                nested.fromTo(frame3.current, {
                    width: 171,
                    left: 114,
                    top: 1422
                }, {
                    width: 143,
                    left: 120,
                    top: 1543,
                    duration: duration
                }, 0);
                nested.fromTo([icons.current, finalCopy.current, getInTouch.current], {
                    opacity: 0
                }, {
                    opacity: 1,
                    duration: duration / 2
                }, duration / 2);

                nested.fromTo(icons.current, {
                    top: 1400
                }, {
                    top: 1300,
                    duration: duration
                }, 0);

                nested.fromTo(finalCopy.current, {
                    top: 1438
                }, {
                    top: 1338,
                    duration: duration
                }, 0);

                nested.fromTo(getInTouch.current, {
                    top: 1542
                }, {
                    top: 1442,
                    duration: duration
                }, 0.1);
                nested.fromTo(restart.current, {
                    bottom: -90
                }, {
                    bottom: -30,
                    opacity: 1,
                    duration: duration / 2
                }, duration / 2);
                nested.fromTo(playhead, {
                    frame: 0
                }, {
                    frame: animation.totalFrames - 1,
                    duration: duration,
                    ease: `steps(${animation.totalFrames - 1})`,
                    onUpdate: () => {
                        animation.goToAndStop(playhead.frame, true);
                    },
                    onStart: () => {
                        if (scene3Act1AniHolder.current && scene3Act1Link.current) {
                            scene3Act1AniHolder.current.className = "videoPart act3Vid";
                            scene3Act1Link.current.className = "act1";
                        }
                        if (scene3Act2AniHolder.current && scene3Act2Link.current) {
                            scene3Act2AniHolder.current.className = "videoPart act3Vid";
                            scene3Act2Link.current.className = "act2";
                        }
                        if (scene3Act3AniHolder.current && scene3Act3Link.current) {
                            scene3Act3AniHolder.current.className = "videoPart act3Part3Vid";
                            scene3Act3Link.current.className = "act3";
                        }

                        if (scene3ClosingAniHolder.current && scene1OpeningAniHolder.current) {
                            scene3ClosingAniHolder.current.className = "videoPart act3Closing visible";
                            scene1OpeningAniHolder.current.className = "videoPart";
                        }
                    },
                    onReverseComplete: () => {
                        if (scene3Act1AniHolder.current && scene3Act1Link.current) {
                            scene3Act1AniHolder.current.className = "videoPart act3Vid";
                            scene3Act1Link.current.className = "act1";
                        }
                        if (scene3Act2AniHolder.current && scene3Act2Link.current) {
                            scene3Act2AniHolder.current.className = "videoPart act3Vid";
                            scene3Act2Link.current.className = "act2";
                        }
                        if (scene3Act3AniHolder.current && scene3Act3Link.current) {
                            scene3Act3AniHolder.current.className = "videoPart act3Part3Vid visible";
                            scene3Act3Link.current.className = "act3 active";
                            setAct("act3");
                        }
                        if (scene3ClosingAniHolder.current && scene1OpeningAniHolder.current) {
                            scene3ClosingAniHolder.current.className = "videoPart act3Closing";
                            scene1OpeningAniHolder.current.className = "lottie";
                        }
                    }
                }, 0);
                //#endregion
                return nested;
            }
        },]
    }, [setAct, setScene, animations]);


    const pixelsForSecond = 500;
    const timeLag = 1.5;
    const loadTimeline = useCallback(() => {
        let scrollTimeout: NodeJS.Timeout;
        if (loadedAniList.length > 0) {
            const ani = loadedAniList.shift();
            if (ani && animations.scene) {
                animations.scene.addLabel(ani.name);
                let nested = ani.nested(ani.loaded);
                animations.scene.add(nested);
                removeAniList.push({ name: ani.name, loaded: ani.loaded, nested: nested });
                setAnimations(animations);
                loadTimeline();
            }
        } else {
            ScrollTrigger.create({
                trigger: ".gws-demo--chrome",
                animation: animations.scene,
                start: 0,
                end: "bottom 100%",
                scrub: 0.001,
                onUpdate: () => {
                    if (nextScene.current && +nextScene.current.style.opacity === 1) {
                        nextScene.current.style.opacity = "0.99";
                        gsap.fromTo(nextScene.current, {
                            bottom: "-25px"
                        }, {
                            bottom: "-90px",
                            opacity: 0,
                            duration: 0.25
                        });
                    }
                },
                onScrubComplete: self => {
                    clearTimeout(scrollTimeout);
                    if (animations.scene.currentLabel().indexOf("Opening") === -1) {
                        if (self.direction > 0) {
                            if (animations.scroller) {
                                animations.scroller.kill();
                            }
                            if (playPauseTrigger.current && playPauseTrigger.current.className.indexOf("pause") === -1) {
                                animations.scroller = gsap.to(window, {
                                    scrollTo: { y: animations.scene.totalDuration() * pixelsForSecond, autoKill: true },
                                    duration: (animations.scene.totalDuration() - animations.scene.time()) * timeLag
                                });
                            }
                        } else {
                            if (window.scrollY > 0) {
                                if (animations.scroller) {
                                    animations.scroller.kill();
                                }
                                scrollTimeout = setTimeout(() => {
                                    animations.scroller = gsap.to(window, {
                                        scrollTo: { y: animations.scene.totalDuration() * pixelsForSecond, autoKill: true },
                                        duration: (animations.scene.totalDuration() - animations.scene.time()) * timeLag
                                    });
                                }, 500);
                            }
                        }

                        setAnimations(animations);
                    } else if (animations.scene.currentLabel() === "scene1OpeningAni") {
                        if (self.direction > 0) {
                            if (animations.scroller) {
                                animations.scroller.kill();
                            }
                            animations.scroller = gsap.to(window, {
                                scrollTo: { y: animations.scene.totalDuration() * pixelsForSecond, autoKill: true },
                                duration: (animations.scene.totalDuration() - animations.scene.time()) * timeLag
                            });
                            setAnimations(animations);
                        }
                    }
                }
            });
            setAniHeight(animations.scene.totalDuration() * pixelsForSecond);
        }

    }, [animations, loadedAniList, removeAniList, setAniHeight]);

    const loadAni = useCallback(() => {
        if (aniList.length > 0) {
            const ani = aniList.shift();
            if (ani && ani.container && ani.container.current) {
                let loaded = lottie.loadAnimation({
                    container: ani.container.current,
                    path: ani.path,
                    renderer: "svg",
                    loop: false,
                    autoplay: false
                });

                if (ani.name === "scene1OpeningAni") {
                    if (ctaArrow.current) {
                        lottie.loadAnimation({
                            container: ctaArrow.current,
                            path: './assets/scene1/Loading/data.json',
                            renderer: "svg",
                            loop: true,
                            autoplay: true
                        });
                    }


                    loaded.addEventListener('DOMLoaded', () => {
                        loaded.removeEventListener('DOMLoaded');
                        animations.scene.addLabel(ani.name);
                        let nested = ani.nested(loaded);
                        nested.paused(false);
                        animations.scene.add(nested);
                        currentAni.push(loaded);

                        // go start loading screen


                        animations.loader.fromTo([heading.current], {
                            opacity: 0
                        }, {
                            opacity: 1,
                            duration: .7
                        }, 0);

                        animations.loader.fromTo([heading.current], {
                            top: -147
                        }, {
                            top: -187,
                            duration: 1
                        }, 0);

                        animations.loader.fromTo([ctalink.current], {
                            opacity: 0
                        }, {
                            opacity: 0,
                            duration: 1
                        }, 0);

                        animations.loader.fromTo([scene2bg.current], {
                            background: "linear-gradient(205deg, #ffffff 30%, #ffffff 50%)"
                        }, {
                            background: "linear-gradient(205deg, #f5f5f5 30%, #e4e3e2 50%)",
                            duration: 0.7
                        }, 0);


                        animations.loader.fromTo([ctaloader.current, book.current, clock.current, lamp.current, mug.current, plant.current, pen.current, desk.current, scene1OpeningAniHolder.current], {
                            opacity: 0
                        }, {
                            opacity: 1,
                            duration: .7
                        }, 0.3);

                        animations.loader.fromTo(scene1LoadProgress.current, {
                            drawSVG: "0%"
                        }, {
                            drawSVG: "0%",
                            duration: 1
                        }, 0);
                        animations.loader.fromTo(scene1LoadProgress.current, {
                            drawSVG: "0%"
                        }, {
                            drawSVG: "100%",
                            duration: 4,

                            onComplete: () => {
                                setTimeout(() => { setScene("loading"); }, 500);
                            }
                        }, 1);

                        setAnimations(animations);

                        animations.loader.play();

                        setTimeout(function () { loadAni(); }, 2000);
                    });
                } else {
                    loaded.addEventListener('data_ready', () => {
                        loaded.removeEventListener('data_ready');
                        loadedAniList.push({
                            name: ani.name,
                            loaded: loaded,
                            nested: ani.nested
                        })
                        loadAni();
                    });
                }
            } else {
                //  alert("no container what?");
            }
        } else {
            // ok, all ani's loaded go bring in the timelines
            loadTimeline();
        }
    }, [aniList, animations, currentAni, loadTimeline, loadedAniList, setScene]);

    useEffect(() => {
        if (window.scrollY > 0) {
            window.scrollTo(0, 0)
        }
    }, [])


    useEffect(() => {

        if (windowDimensions.width < 1080 || windowDimensions.height < 720) {
            setSceneHeight(windowDimensions.height - 20 - 35);
        } else if (windowDimensions.height > 1200) {
            setSceneHeight(windowDimensions.height - 240 - 47);
        } else {
            setSceneHeight(windowDimensions.height - 120 - 47);
        }

    }, [windowDimensions.width, windowDimensions.height])

    const startAct = useCallback((label: string | null = null, seconds: number | null = null) => {
        if (animations.scroller) {
            animations.scroller.kill();
        }
       
        if (label) {
            window.scrollTo(0, (animations.scene.labels[label]) * pixelsForSecond);
        } else if (seconds) {
            window.scrollTo(0, (seconds * pixelsForSecond));
        }
        gsap.fromTo([lottieFlash.current], {
            opacity: 0
        }, {
            opacity: .8,
            yoyo: true,
            repeat: 1,
            duration: 0.25
        });

        setAnimations(animations);
    }, [animations]);


    const startAnimation = useCallback(() => {
        animations.scroller = gsap.fromTo(window, {
            scrollTo: { y: 0 }
        }, {
            scrollTo: { y: animations.scene.totalDuration() * pixelsForSecond, autoKill: true },
            duration: (animations.scene.totalDuration()) * timeLag
        });

        setAnimations(animations);
    }, [animations]);

    useEffect(() => {

        if (gotoScene !== "") {
            if (gotoScene === "scene2OpeningAni") {
                startAct(null, 64);
            } else if (gotoScene === "scene3OpeningAni") {
                startAct(null, 121);
            } else if (gotoScene === "scene1OpeningAni") {
                startAnimation();
            } else {
                startAct(gotoScene);
            }
            setGotoScene("");
        }
    }, [gotoScene, setGotoScene, startAct, scene, startAnimation]);

    useEffect(() => {
        if (scene1OpeningAniHolder.current &&
            scene1Act1AniHolder.current &&
            scene1Act2AniHolder.current &&
            scene1Act3AniHolder.current) {
            if (!animationsLoading) {
                setAnimationsLoading(true);
                loadAni();
            }
        }

    }, [animations, animationsLoading, loadAni, removeAniList]);


    return (
        <>
            <div className="gws-demo--crop">
                <div className="gws-demo--crop--2 gws-demo--scene-1--bg" ref={scene2bg}>
                    <div className="gws-demo--animations" style={{ top: sceneHeight / 2 - 20 }}>
                        <div className={"lottieflash " + scene + " " + act} ref={lottieFlash}></div>
                        <div className="videoPart act2Opening" ref={scene2OpeningAniHolder}></div>
                        <div className="videoPart act3Opening" ref={scene3OpeningAniHolder}></div>
                        <div className="playPauseHolder" ref={playPauseTrigger} onClick={() => {
                            if (playPauseTrigger.current && playPauseTrigger.current.className.indexOf("pause") === -1) {
                                // pause
                                sendPostMessage("Google workspace demo: pause", scene === "connect" ? "Create in real time" : "Collaborate from anywhere", "in-page interaction");
                                if (playPauseTrigger.current) {
                                    playPauseTrigger.current.className = "playPauseHolder pause"
                                }
                            } else {
                                //play
                                sendPostMessage("Google workspace demo: play", scene === "connect" ? "Create in real time" : "Collaborate from anywhere", "in-page interaction");

                                if (playPauseTrigger.current) {
                                    playPauseTrigger.current.className = "playPauseHolder"
                                }

                                animations.scroller = gsap.to(window, {
                                    scrollTo: { y: animations.scene.totalDuration() * pixelsForSecond, autoKill: true },
                                    duration: (animations.scene.totalDuration() - animations.scene.time()) * timeLag
                                });
                                gsap.fromTo(nextScene.current, {
                                    bottom: "-25px"
                                }, {
                                    bottom: "-90px",
                                    opacity: 0,
                                    duration: 0.25
                                });
                                setAnimations(animations);
                            }


                        }}>
                            <img className="playButton" ref={playButton} src="./assets/play.svg" alt="play"></img>
                            <img className="pauseButton" ref={pauseButton} src="./assets/pause.svg" alt="pause" ></img>
                        </div>
                    </div>
                    <div className="gws-demo--transition-bg" ref={transitionBg}></div>
                    <div className="gws-demo--transition-frame" ref={transitionFrame}></div>
                    <div className="gws-demo--frame" ref={gwsDemoFrame}>
                        <div className={"gws-demo--scene-1 " + act} style={{ top: sceneHeight / 2 - 20 }}>
                            <h1 ref={heading}>See how it’s done with Google Workspace</h1>
                            <div className="ctaloader" ref={ctaloader}><svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="16" cy="16" r="15" stroke="#BDC1C6" strokeWidth="2" />
                                <circle cx="16" cy="16" r="15" stroke="#1A73E8" strokeWidth="2" ref={scene1LoadProgress} transform="rotate(-90) translate(-32)" />

                            </svg></div>
                            <a className="ctalink" href="#connect" ref={ctalink} onClick={(ev) => {
                                ev.preventDefault();
                                sendPostMessage("Google workspace demo: hero", "take the tour", "in-page interaction");
                                startAnimation();
                            }}>
                                <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="16" cy="16" r="15" stroke="#1A73E8" strokeWidth="2" />
                                </svg>
                                <div ref={ctaArrow}></div>
                                Take the tour</a>
                            <img className="book" ref={book} src="./assets/scene1/book.png" alt="Book"></img>
                            <img className="clock" ref={clock} src="./assets/scene1/clock.png" alt="Clock"></img>
                            <img className="lamp" ref={lamp} src="./assets/scene1/lamp.png" alt="Lamp"></img>
                            <img className="mug" ref={mug} src="./assets/scene1/mug.png" alt="Mug"></img>
                            <img className="plant" ref={plant} src="./assets/scene1/plant.png" alt="Plant"></img>
                            <img className="pen" ref={pen} src="./assets/scene1/pen.png" alt="Pen"></img>

                            <div className="lottie" ref={scene1OpeningAniHolder}></div>

                            <div className="videoPart" ref={scene1Act1AniHolder}></div>
                            <div className="videoPart" ref={scene1Act2AniHolder}></div>
                            <div className="videoPart" ref={scene1Act3AniHolder}></div>
                            <div className="nav" ref={scene1Nav}>
                                <a href="#scene1act1" ref={scene1Act1Link} className="act1 active" onClick={(ev) => {
                                    ev.preventDefault();
                                    sendPostMessage("Google workspace demo: interaction", "Connect with your team : Connect distributed teams", "in-page interaction");
                                    setDontTrack(true);
                                    startAct("scene1Act1Ani");
                                }}>
                                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="16" cy="16" r="15" stroke="#BDC1C6" strokeWidth="2" />
                                        <circle cx="16" cy="16" r="15" stroke="#1A73E8" strokeWidth="2" ref={scene1Act1Progress} transform="rotate(-90) translate(-32)" />
                                        <path d="M22 13H20.6667V19H12V20.3333C12 20.7 12.3 21 12.6667 21H20L22.6667 23.6666V13.6666C22.6667 13.3 22.3667 13 22 13ZM19.3333 17V11C19.3333 10.6333 19.0333 10.3333 18.6667 10.3333H9.99999C9.63333 10.3333 9.33333 10.6333 9.33333 11V20.3333L12 17.6666H18.6667C19.0333 17.6666 19.3333 17.3666 19.3333 17Z" fill="#BDC1C6" />
                                    </svg>
                                    <span ref={scene1Act1} >Connect distributed teams</span></a>

                                <a href="#scene1act2" ref={scene1Act2Link} className="act2" onClick={(ev) => {
                                    ev.preventDefault();
                                    sendPostMessage("Google workspace demo: interaction", "Connect with your team : Empower everyone to contribute", "in-page interaction");
                                    setDontTrack(true);
                                    startAct("scene1Act2Ani")
                                }}>
                                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="16" cy="16" r="15" stroke="#BDC1C6" strokeWidth="2" />
                                        <circle cx="16" cy="16" r="15" stroke="#1A73E8" strokeWidth="2" ref={scene1Act2Progress} transform="rotate(-90) translate(-32)" />
                                        <path d="M22.6667 11.4533L15.06 19.0666L12.2333 16.24L13.1733 15.3L15.06 17.1866L21.7267 10.52L22.6667 11.4533ZM16 21.3333C13.06 21.3333 10.6667 18.94 10.6667 16C10.6667 13.06 13.06 10.6666 16 10.6666C17.0467 10.6666 18.0267 10.9733 18.8533 11.5L19.82 10.5333C18.7333 9.77998 17.42 9.33331 16 9.33331C12.32 9.33331 9.33333 12.32 9.33333 16C9.33333 19.68 12.32 22.6666 16 22.6666C17.1533 22.6666 18.24 22.3733 19.1867 21.8533L18.1867 20.8533C17.52 21.16 16.78 21.3333 16 21.3333ZM20.6667 18H18.6667V19.3333H20.6667V21.3333H22V19.3333H24V18H22V16H20.6667V18Z" fill="#BDC1C6" />
                                    </svg>
                                    <span ref={scene1Act2}>Empower everyone<br />to contribute</span></a>

                                <a href="#scene1act3" ref={scene1Act3Link} className="act3" onClick={(ev) => {
                                    ev.preventDefault();
                                    sendPostMessage("Google workspace demo: interaction", "Connect with your team : Get on the same page", "in-page interaction");
                                    setDontTrack(true);
                                    startAct("scene1Act3Ani")
                                }}>
                                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="16" cy="16" r="15" stroke="#BDC1C6" strokeWidth="2" />
                                        <circle cx="16" cy="16" r="15" stroke="#1A73E8" strokeWidth="2" ref={scene1Act3Progress} transform="rotate(-90) translate(-32)" />
                                        <path d="M13.3333 18.6666H18.6667V20H13.3333V18.6666ZM13.3333 16H18.6667V17.3333H13.3333V16ZM17.3333 9.33331H12C11.2667 9.33331 10.6667 9.93331 10.6667 10.6666V21.3333C10.6667 22.0666 11.26 22.6666 11.9933 22.6666H20C20.7333 22.6666 21.3333 22.0666 21.3333 21.3333V13.3333L17.3333 9.33331ZM20 21.3333H12V10.6666H16.6667V14H20V21.3333Z" fill="#BDC1C6" />
                                    </svg>
                                    <span ref={scene1Act3}>Get on the<br />same page</span></a>
                                <svg width="189" height="191" viewBox="0 0 189 191" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <line x1="17" y1="111" x2="17" y2="157" stroke="#BDC1C6" strokeWidth="2" className="lineAct3" />
                                    <line x1="17" y1="32" x2="17" y2="81" stroke="#BDC1C6" strokeWidth="2" className="lineAct2" />
                                </svg>
                            </div>
                            <div className="desk" ref={desk}></div>


                            <img className="headphones" ref={headphones} src="./assets/scene2/headphones.png" alt="Headphones"></img>
                            <img className="notebook" ref={notebook} src="./assets/scene2/notebook.png" alt="Notebook"></img>
                            <img className="pens" ref={pens} src="./assets/scene2/pens.png" alt="Pens"></img>
                            <img className="tablet" ref={tablet} src="./assets/scene2/tablet.png" alt="Tablet"></img>
                            <img className="tray" ref={tray} src="./assets/scene2/tray.png" alt="Tray"></img>
                            <img className="watch" ref={watch} src="./assets/scene2/watch.png" alt="Watch"></img>

                            <div className="videoPart act2Vid" ref={scene2Act1AniHolder} ></div>
                            <div className="videoPart act2Vid" ref={scene2Act2AniHolder}></div>
                            <div className="videoPart act2Vid" ref={scene2Act3AniHolder}></div>

                            <div className="nav act2nav" ref={scene2Nav}>
                                <a href="#scene2act1" ref={scene2Act1Link} className="act1 active" onClick={(ev) => {
                                    ev.preventDefault();
                                    sendPostMessage("Google workspace demo: interaction", "Create in real time: Share rich content remotely", "in-page interaction");
                                    setDontTrack(true);
                                    startAct(null, 64);
                                }}>
                                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="16" cy="16" r="15" stroke="#BDC1C6" strokeWidth="2" />
                                        <circle cx="16" cy="16" r="15" stroke="#1A73E8" strokeWidth="2" ref={scene2Act1Progress} transform="rotate(-90) translate(-32)" />
                                        <path d="M17.3333 11.6666L18.86 13.1933L16.94 15.1133L17.8867 16.06L19.8067 14.14L21.3333 15.6666V11.6666H17.3333ZM14.6667 11.6666H10.6667V15.6666L12.1933 14.14L15.3333 17.2733V22.3333H16.6667V16.7266L13.14 13.1933L14.6667 11.6666Z" fill="#BDC1C6" />
                                    </svg>


                                    <span ref={scene2Act1} >Share rich<br />content remotely</span></a>

                                <a href="#scene2act2" ref={scene2Act2Link} className="act2" onClick={(ev) => {
                                    ev.preventDefault();
                                    sendPostMessage("Google workspace demo: interaction", "Create in real time: Give everyone a voice", "in-page interaction");
                                    setDontTrack(true);
                                    startAct(null, 85.5)
                                }}>
                                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="16" cy="16" r="15" stroke="#BDC1C6" strokeWidth="2" />
                                        <circle cx="16" cy="16" r="15" stroke="#1A73E8" strokeWidth="2" ref={scene2Act2Progress} transform="rotate(-90) translate(-32)" />
                                        <path d="M14 22C14 22.3667 14.3 22.6667 14.6667 22.6667H17.3333C17.7 22.6667 18 22.3667 18 22V21.3334H14V22ZM16 9.33337C13.4267 9.33337 11.3333 11.4267 11.3333 14C11.3333 15.5867 12.1267 16.98 13.3333 17.8267V19.3334C13.3333 19.7 13.6333 20 14 20H18C18.3667 20 18.6667 19.7 18.6667 19.3334V17.8267C19.8733 16.98 20.6667 15.5867 20.6667 14C20.6667 11.4267 18.5733 9.33337 16 9.33337ZM17.3333 17.1334V18.6667H14.6667V17.1334C13.6533 16.42 12.6667 15.6867 12.6667 14C12.6667 12.16 14.16 10.6667 16 10.6667C17.84 10.6667 19.3333 12.16 19.3333 14C19.3333 15.66 18.3267 16.4334 17.3333 17.1334Z" fill="#BDC1C6" />
                                    </svg>

                                    <span ref={scene2Act2}>Give everyone<br />a voice</span></a>

                                <a href="#scene2act3" ref={scene2Act3Link} className="act3" onClick={(ev) => {
                                    ev.preventDefault();
                                    sendPostMessage("Google workspace demo: interaction", "Create in real time : Organize next steps", "in-page interaction");
                                    setDontTrack(true);
                                    startAct(null, 101.5)
                                }}>
                                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="16" cy="16" r="15" stroke="#BDC1C6" strokeWidth="2" />
                                        <circle cx="16" cy="16" r="15" stroke="#1A73E8" strokeWidth="2" ref={scene2Act3Progress} transform="rotate(-90) translate(-32)" />
                                        <path d="M16 10.6666L15.06 11.6066L18.78 15.3333H10.6667V16.6666H18.78L15.06 20.3933L16 21.3333L21.3333 16L16 10.6666Z" fill="#BDC1C6" />
                                    </svg>

                                    <span ref={scene2Act3}>Organize <br />next steps</span></a>

                                <svg width="189" height="191" viewBox="0 0 189 191" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <line x1="17" y1="111" x2="17" y2="157" stroke="#BDC1C6" strokeWidth="2" className="lineAct3" />
                                    <line x1="17" y1="32" x2="17" y2="81" stroke="#BDC1C6" strokeWidth="2" className="lineAct2" />
                                </svg>
                            </div>



                            <img className="egg" ref={egg} src="./assets/scene3/egg.png" alt="Egg"></img>
                            <img className="lightshade" ref={lightshade} src="./assets/scene3/lightshade.png" alt="Lightshade"></img>
                            <img className="mobile" ref={mobile} src="./assets/scene3/mobile.png" alt="Mobile"></img>
                            <img className="mugcafe" ref={mugcafe} src="./assets/scene3/mugcafe.png" alt="Mugcafe"></img>
                            <img className="pegboard" ref={pegboard} src="./assets/scene3/pegboard.png" alt="Pegboard"></img>
                            <img className="sugarcube" ref={sugarcube} src="./assets/scene3/sugarcube.png" alt="Sugarcube"></img>
                            <img className="teapot" ref={teapot} src="./assets/scene3/teapot.png" alt="Teapot"></img>
                            <img className="tablenumber" ref={tablenumber} src="./assets/scene3/tablenumber.png" alt="Tablenumber"></img>

                            <div className="videoPart act3Vid" ref={scene3Act1AniHolder}></div>
                            <div className="videoPart act3Vid" ref={scene3Act2AniHolder}></div>
                            <div className="videoPart act3Part3Vid" ref={scene3Act3AniHolder}></div>
                            <div className="videoPart act3Part3Vid" ref={scene3ClosingAniHolder} ></div>


                            <img className="frame1" ref={frame1} src="./assets/scene3/frame1.png" alt="frame1"></img>
                            <img className="frame2" ref={frame2} src="./assets/scene3/frame2.png" alt="frame2"></img>
                            <img className="frame3" ref={frame3} src="./assets/scene3/frame3.png" alt="Frame3"></img>
                            <img className="laptop" ref={laptop} src="./assets/scene3/laptop.png" alt="laptop"></img>
                            <img className="icons" ref={icons} src="./assets/scene1/icons.svg" alt="icons"></img>

                            <h2 className="finalCopy" ref={finalCopy}>Connect, create, and collaborate<br />with your team</h2>

                            <a href="https://workspace.google.com/intl/en_au/contact/?source=gafb-contact-globalnav-en-AU" className="get-in-touch" ref={getInTouch} onClick={(ev) => {
                                sendPostMessage("Google workspace demo", "contact sales", "footer");
                                setDontTrack(true);
                            }}>Get in touch to learn how it's done</a>




                            <div className="nav act3nav" ref={scene3Nav}>
                                <a href="#scene3act1" ref={scene3Act1Link} className="act1 active" onClick={(ev) => {
                                    ev.preventDefault();
                                    sendPostMessage("Google workspace demo: interaction", "Collaborate from anywhere : Stay on task across devices", "in-page interaction");
                                    setDontTrack(true);
                                    startAct(null, 121);
                                }}>
                                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="16" cy="16" r="15" stroke="#BDC1C6" strokeWidth="2" />
                                        <circle cx="16" cy="16" r="15" stroke="#1A73E8" strokeWidth="2" ref={scene3Act1Progress} transform="rotate(-90) translate(-32)" />
                                        <path d="M16 10.6667L15.06 11.6067L18.78 15.3334H10.6666V16.6667H18.78L15.06 20.3934L16 21.3334L21.3333 16.0001L16 10.6667Z" fill="#BDC1C6" />
                                    </svg>

                                    <span ref={scene3Act1} >Stay on task<br />across devices</span></a>

                                <a href="#scene3act2" ref={scene3Act2Link} className="act2" onClick={(ev) => {
                                    ev.preventDefault();
                                    sendPostMessage("Google workspace demo: interaction", "Collaborate from anywhere : Work with all file types", "in-page interaction");
                                    setDontTrack(true);
                                    startAct(null, 141.8)
                                }}>
                                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="16" cy="16" r="15" stroke="#BDC1C6" strokeWidth="2" />
                                        <circle cx="16" cy="16" r="15" stroke="#1A73E8" strokeWidth="2" ref={scene3Act2Progress} transform="rotate(-90) translate(-32)" />
                                        <path d="M21.333 11.9998H15.9997L14.6663 10.6665H10.6663C9.93301 10.6665 9.33967 11.2665 9.33967 11.9998L9.33301 19.9998C9.33301 20.7332 9.93301 21.3332 10.6663 21.3332H21.333C22.0663 21.3332 22.6663 20.7332 22.6663 19.9998V13.3332C22.6663 12.5998 22.0663 11.9998 21.333 11.9998ZM21.333 19.9998H10.6663V13.3332H21.333V19.9998Z" fill="#BDC1C6" />
                                    </svg>

                                    <span ref={scene3Act2}>Work with<br />all file types</span></a>

                                <a href="#scene3act3" ref={scene3Act3Link} className="act3" onClick={(ev) => {
                                    ev.preventDefault();
                                    sendPostMessage("Google workspace demo: interaction", "Collaborate from anywhere : Achieve more together", "in-page interaction");
                                    setDontTrack(true);
                                    startAct(null, 179)
                                }}>
                                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="16" cy="16" r="15" stroke="#BDC1C6" strokeWidth="2" />
                                        <circle cx="16" cy="16" r="15" stroke="#1A73E8" strokeWidth="2" ref={scene3Act3Progress} transform="rotate(-90) translate(-32)" />
                                        <path d="M13.9999 18.8001L11.1999 16.0001L10.2666 16.9334L13.9999 20.6667L21.9999 12.6667L21.0666 11.7334L13.9999 18.8001Z" fill="#BDC1C6" />
                                    </svg>

                                    <span ref={scene3Act3}>Achieve more together</span></a>

                                <svg width="189" height="191" viewBox="0 0 189 191" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <line x1="17" y1="111" x2="17" y2="157" stroke="#BDC1C6" strokeWidth="2" className="lineAct3" />
                                    <line x1="17" y1="32" x2="17" y2="81" stroke="#BDC1C6" strokeWidth="2" className="lineAct2" />
                                </svg>
                            </div>

                            <div className="deskAct3" ref={deskAct3}></div>



                        </div>
                    </div>
                </div>
            </div>
            <button className="restart" ref={restart} onClick={() => {
                sendPostMessage("Google workspace demo: footer", "restart tour", "in-page interaction");
                setDontTrack(true);
                window.scrollTo(0, 0);

            }}>
                <svg width="162" height="75" viewBox="5 0 162 75" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="81" cy="81" r="81" fill="white" />
                    <path d="M86.7082 19.2918C85.4999 18.0835 83.8416 17.3335 81.9999 17.3335C78.3166 17.3335 75.3416 20.3168 75.3416 24.0002C75.3416 27.6835 78.3166 30.6668 81.9999 30.6668C85.1082 30.6668 87.6999 28.5418 88.4416 25.6668H86.7082C86.0249 27.6085 84.1749 29.0002 81.9999 29.0002C79.2416 29.0002 76.9999 26.7585 76.9999 24.0002C76.9999 21.2418 79.2416 19.0002 81.9999 19.0002C83.3832 19.0002 84.6166 19.5752 85.5166 20.4835L82.8332 23.1668H88.6666V17.3335L86.7082 19.2918Z" fill="#5F6368" />
                </svg>
                <span>Restart tour</span>
            </button>
            <button className="continue" ref={nextScene} onClick={() => {

                sendPostMessage("Google workspace demo: continue", scene === "connect" ? "Create in real time" : "Collaborate from anywhere", "in-page interaction");


                animations.scroller = gsap.to(window, {
                    scrollTo: { y: animations.scene.totalDuration() * pixelsForSecond, autoKill: true },
                    duration: (animations.scene.totalDuration() - animations.scene.time()) * timeLag
                });
                gsap.fromTo(nextScene.current, {
                    bottom: "-25px"
                }, {
                    bottom: "-90px",
                    opacity: 0,
                    duration: 0.25
                });
                setAnimations(animations);
            }}>
                <svg width="162" height="75" viewBox="5 0 162 75" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="81" cy="81" r="81" fill="white" />
                    <path d="M87.6663 28.9997L86.4913 27.8247L81.833 32.4747V22.333H80.1663V32.4747L75.5163 27.8163L74.333 28.9997L80.9997 35.6663L87.6663 28.9997Z" fill="#5F6368" />
                </svg><span>Continue</span>
            </button>


        </>

    );
}

export default Scene1;


